import { ThreadMessageRole } from "@brookbush/database";
import type { ThreadExpanded } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import type { UIMessage } from "ai";
import { useMemo } from "react";
import useSWR from "swr";

import fetcher from "~/utils/fetcher.client";

const useThread = (threadId: number | null) => {
	const { userId } = useAuth();

	const {
		data: thread,
		isLoading,
		mutate,
	} = useSWR<ThreadExpanded>(
		userId && threadId ? `/threads/${threadId}` : null,
		(url: string) => fetcher<ThreadExpanded>(url, { api: "chat" }),
	);

	const initialMessages = useMemo<UIMessage[]>(
		() =>
			(thread?.threadMessages ?? []).map((message) => {
				const parts: any[] = [{ type: "text", text: message.content }];

				const metadata = message.metadata as any;
				if (metadata?.toolCalls) {
					metadata.toolCalls.forEach((toolCall: any) => {
						const toolResult = metadata?.toolResults?.find(
							(tr: any) => tr.toolCallId === toolCall.toolCallId,
						);

						parts.push({
							type: "tool-invocation",
							toolInvocation: {
								state: "result",
								toolCallId: toolCall.toolCallId,
								toolName: toolCall.toolName,
								args: toolCall.args || toolCall.input,
								result: toolResult
									? toolResult.result || toolResult.output
									: undefined,
							},
						});
					});
				}

				return {
					id: String(message.id),
					role:
						message.role === ThreadMessageRole.ASSISTANT ? "assistant" : "user",
					metadata: message.metadata ?? undefined,
					parts,
				} as UIMessage;
			}),
		[thread],
	);

	return { thread, initialMessages, isLoading, mutate };
};

export default useThread;
